/**
 * announce() — the app's one polite live region.
 *
 * A live region has to exist *before* the text lands in it, so the naive shape is
 * to ship one per control. In a virtualised grid that is catastrophic: 32 rows on
 * screen meant 32 empty `aria-live` nodes being created and destroyed on every
 * scroll frame, which is precisely the pattern assistive tech handles worst.
 *
 * So there is exactly one node, appended to `<body>` on first use and never
 * removed, and every transient confirmation in the product speaks through it.
 * Toasts remain the *visible* channel; this is the spoken one for the feedback
 * that is purely visual (an icon swapping to a check, say).
 */

const CLEAR_AFTER_MS = 2_000;

let node: HTMLElement | null = null;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

function region(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  if (node !== null && node.isConnected) return node;

  const created = document.createElement('div');
  created.id = 'af-live-region';
  created.className = 'sr-only';
  created.setAttribute('role', 'status');
  created.setAttribute('aria-live', 'polite');
  created.setAttribute('aria-atomic', 'true');
  document.body.appendChild(created);
  node = created;
  return created;
}

/**
 * Say something once, politely. Repeating the identical string still speaks:
 * screen readers announce a live region only when its text *changes*, so the
 * node is blanked first and refilled on the next frame.
 */
export function announce(message: string): void {
  const target = region();
  if (target === null || message === '') return;

  target.textContent = '';
  if (clearTimer !== null) clearTimeout(clearTimer);

  const fill = () => {
    target.textContent = message;
    clearTimer = setTimeout(() => {
      target.textContent = '';
      clearTimer = null;
    }, CLEAR_AFTER_MS);
  };

  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fill);
  else fill();
}
